import React, { createContext, useContext, useMemo, useState } from "react";

// Ephemeral store for TradeOS form state during a single app session.
// Intentionally NOT persisted to localStorage/sessionStorage so a full
// browser refresh clears all values.

const TradeOSContext = createContext(null);

export const TradeOSProvider = ({ children }) => {
  // Store multiple independent states keyed by a scope string, e.g. "client-ior", "admin-ior"
  const [store, setStore] = useState({});

  const value = useMemo(() => ({
    getFormState: (scope) => store[scope] || null,
    setFormState: (scope, data) =>
      setStore((prev) => ({ ...prev, [scope]: data })),
    clearFormState: (scope) =>
      setStore((prev) => {
        const next = { ...prev };
        delete next[scope];
        return next;
      }),
    clearAll: () => setStore({}),
  }), [store]);

  return <TradeOSContext.Provider value={value}>{children}</TradeOSContext.Provider>;
};

export const useTradeOSContext = () => {
  const ctx = useContext(TradeOSContext);
  if (!ctx) throw new Error("useTradeOSContext must be used within TradeOSProvider");
  return ctx;
};

// Convenience hook to bind a component to a scoped in-memory form state
export const useScopedFormState = (scope, data) => {
  const { getFormState, setFormState, clearFormState } = useTradeOSContext();
  const saved = getFormState(scope);
  const save = (next) => setFormState(scope, next);
  const clear = () => clearFormState(scope);
  return { saved, save, clear };
};

export default TradeOSContext;
